import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { HeartHandshake, MapPin, Search, DollarSign, Package, Building2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PublicNav } from "@/components/PublicNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PRESETS = [10, 25, 50, 100];

export default function Give() {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState(null); // "money" | "items"
  const [org, setOrg] = useState(null);
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [items, setItems] = useState("");
  const [note, setNote] = useState("");
  const [donor, setDonor] = useState({ donor_name: "", donor_email: "" });
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const { data } = await api.get("/orgs/public");
    setOrgs(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/direct/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Thank you! 💚 Your donation has been received.");
          setParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") { setParams({}, { replace: true }); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const openMoney = (o) => { setOrg(o); setMode("money"); setAmount(25); setCustom(""); setDonor({ donor_name: "", donor_email: "" }); };
  const openItems = (o) => { setOrg(o); setMode("items"); setItems(""); setNote(""); setDonor({ donor_name: "", donor_email: "" }); };
  const close = () => { setMode(null); setOrg(null); };

  const finalAmount = custom ? parseFloat(custom) : amount;

  const donateMoney = async () => {
    if (!finalAmount || finalAmount < 1) { toast.error("Please enter at least $1"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/direct/checkout", {
        org_id: org.id, amount: finalAmount, origin_url: window.location.origin, ...donor,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const pledgeItems = async () => {
    if (!items.trim()) { toast.error("Please describe what you'd like to donate"); return; }
    setLoading(true);
    try {
      await api.post("/pledges", { org_id: org.id, items, note, ...donor });
      toast.success("Pledge sent! The organization has been notified and will coordinate with you.");
      close();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const filtered = orgs.filter((o) => {
    const t = q.toLowerCase();
    return !t || o.org_name.toLowerCase().includes(t) || (o.city || "").toLowerCase().includes(t);
  });

  return (
    <div className="min-h-screen bg-stone-50">
      <PublicNav />

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-8">
        <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-emerald-800 bg-emerald-50 px-4 py-1.5 rounded-full">
          <HeartHandshake className="w-3.5 h-3.5" /> Give directly
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900 mt-6 max-w-3xl">
          No specific listing you want to fund? Support an organization directly.
        </h1>
        <p className="text-stone-600 mt-4 max-w-2xl leading-relaxed">
          Choose any verified aid organization below. Make a secure monetary gift, or pledge to drop off
          general staples they always need. Your gift goes <span className="font-medium text-stone-900">directly to the organization you choose</span> and is credited to their totals.
        </p>

        <div className="relative mt-8 max-w-md">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="give-org-filter" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by organization or city…" className="rounded-full pl-9 h-11 border-stone-200 bg-white" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="give-orgs-grid">
          {filtered.length === 0 && (
            <div className="col-span-full bg-white rounded-2xl border border-dashed border-stone-300 p-12 text-center text-stone-500">
              No verified organizations found. Check back soon.
            </div>
          )}
          {filtered.map((o) => (
            <div key={o.id} data-testid={`give-org-${o.id}`} className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><Building2 className="w-5 h-5 text-emerald-800" /></div>
                {o.is_warming_shelter && <Badge variant="outline" className="rounded-full bg-orange-50 text-orange-800 border-orange-200">Warming shelter</Badge>}
              </div>
              <h3 className="font-heading text-lg font-medium text-stone-900 mt-4">{o.org_name}</h3>
              {(o.city || o.address) && <p className="flex items-center gap-1 text-sm text-stone-500 mt-1"><MapPin className="w-3.5 h-3.5" /> {o.address || o.city}{o.city && o.address ? `, ${o.city}` : ""}</p>}
              {o.services?.length > 0 && <p className="text-sm text-stone-500 mt-2">{o.services.slice(0, 4).join(" · ")}</p>}
              <div className="mt-5 pt-5 border-t border-stone-100 flex gap-2">
                <Button data-testid={`give-money-${o.id}`} onClick={() => openMoney(o)} className="flex-1 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">
                  <DollarSign className="w-4 h-4 mr-1.5" /> Donate
                </Button>
                <Button data-testid={`give-items-${o.id}`} onClick={() => openItems(o)} variant="outline" className="flex-1 rounded-full border-stone-300">
                  <Package className="w-4 h-4 mr-1.5" /> Pledge items
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Money dialog */}
      <Dialog open={mode === "money"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Donate to {org?.org_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase text-stone-500">Choose an amount</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {PRESETS.map((p) => (
                  <button key={p} data-testid={`give-amount-${p}`} onClick={() => { setAmount(p); setCustom(""); }}
                    className={`h-11 rounded-xl border text-sm font-medium transition-all ${!custom && amount === p ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-stone-700 border-stone-200 hover:border-emerald-300"}`}>
                    ${p}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Or enter a custom amount</Label>
              <div className="relative">
                <DollarSign className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="give-custom-amount" type="number" min="1" step="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom amount" className="rounded-xl h-11 pl-9" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Your name (optional)</Label>
              <Input data-testid="give-donor-name" value={donor.donor_name} onChange={(e) => setDonor((d) => ({ ...d, donor_name: e.target.value }))} placeholder="Jane Doe" className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Email for receipt (optional)</Label>
              <Input data-testid="give-donor-email" type="email" value={donor.donor_email} onChange={(e) => setDonor((d) => ({ ...d, donor_email: e.target.value }))} placeholder="jane@email.com" className="rounded-xl h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="give-money-confirm" onClick={donateMoney} disabled={loading} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-11">
              {loading ? "Redirecting to payment…" : `Donate $${Number(finalAmount || 0).toFixed(2)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Items pledge dialog */}
      <Dialog open={mode === "items"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Pledge items to {org?.org_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              Commit to dropping off general staples this organization always needs. They'll be notified and reach out to coordinate.
            </p>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">What will you donate?</Label>
              <Textarea data-testid="give-items-text" value={items} onChange={(e) => setItems(e.target.value)} placeholder="e.g. 5 cases of canned goods, 20 hygiene kits, winter coats" className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Note (optional)</Label>
              <Textarea data-testid="give-items-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="When you'd like to drop off, questions, etc." className="rounded-xl" />
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-2">
                <Label className="text-xs tracking-widest uppercase text-stone-500">Your name</Label>
                <Input data-testid="give-pledge-name" value={donor.donor_name} onChange={(e) => setDonor((d) => ({ ...d, donor_name: e.target.value }))} placeholder="Jane Doe" className="rounded-xl h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-widest uppercase text-stone-500">Email (optional)</Label>
                <Input data-testid="give-pledge-email" type="email" value={donor.donor_email} onChange={(e) => setDonor((d) => ({ ...d, donor_email: e.target.value }))} placeholder="jane@email.com" className="rounded-xl h-11" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="give-items-confirm" onClick={pledgeItems} disabled={loading} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-11">
              {loading ? "Sending…" : "Send pledge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
